'use client';

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useConnectorStore } from '@/lib/connectors/store';
import type { AuthType, AuthConfig } from '@/lib/connectors/types';
import { Key, Lock, Shield, Globe } from 'lucide-react';

const AUTH_TYPES: { value: AuthType; label: string; description: string; icon: React.ComponentType<{ className?: string }> }[] = [
  { value: 'none', label: 'None', description: 'No authentication required', icon: Shield },
  { value: 'api_key', label: 'API Key', description: 'API key in header or query', icon: Key },
  { value: 'oauth2', label: 'OAuth 2.0', description: 'OAuth 2.0 flow', icon: Globe },
  { value: 'basic', label: 'Basic', description: 'Username & password', icon: Lock },
  { value: 'bearer', label: 'Bearer', description: 'Bearer token', icon: Shield },
];

export function AuthConfigPanel() {
  const { currentConnector, setCurrentConnector } = useConnectorStore();

  const authType = currentConnector?.auth_type || 'none';
  const authConfig = currentConnector?.auth_config || {};

  const updateAuthType = (type: AuthType) => {
    if (!currentConnector) return;
    setCurrentConnector({
      ...currentConnector,
      auth_type: type,
      auth_config: {},
    });
  };

  const updateAuthConfig = (updates: Partial<AuthConfig>) => {
    if (!currentConnector) return;
    setCurrentConnector({
      ...currentConnector,
      auth_config: { ...authConfig, ...updates },
    });
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Authentication Type</CardTitle>
          <CardDescription>
            Choose how users will authenticate with this connector
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {AUTH_TYPES.map((type) => (
              <button
                key={type.value}
                type="button"
                onClick={() => updateAuthType(type.value)}
                className={`p-4 border rounded-lg text-left transition-colors ${
                  authType === type.value
                    ? 'border-primary bg-primary/5'
                    : 'hover:border-muted-foreground/50'
                }`}
              >
                <div className="flex items-center gap-2 mb-2">
                  <type.icon className="size-4" />
                  <span className="font-medium">{type.label}</span>
                </div>
                <p className="text-xs text-muted-foreground">
                  {type.description}
                </p>
              </button>
            ))}
          </div>
        </CardContent>
      </Card>

      {authType === 'api_key' && (
        <Card>
          <CardHeader>
            <CardTitle>API Key Configuration</CardTitle>
            <CardDescription>
              Configure how the API key is sent with requests
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="api-key-name">Header/Query Name</Label>
                <Input
                  id="api-key-name"
                  value={authConfig.api_key_name || ''}
                  onChange={(e) => updateAuthConfig({ api_key_name: e.target.value })}
                  placeholder="X-API-Key"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="api-key-prefix">Prefix (optional)</Label>
                <Input
                  id="api-key-prefix"
                  value={authConfig.api_key_prefix || ''}
                  onChange={(e) => updateAuthConfig({ api_key_prefix: e.target.value })}
                  placeholder="Bearer"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Location</Label>
              <div className="flex gap-4">
                {(['header', 'query'] as const).map((loc) => (
                  <button
                    key={loc}
                    type="button"
                    onClick={() => updateAuthConfig({ api_key_location: loc })}
                    className={`px-4 py-2 border rounded-lg capitalize ${
                      (authConfig.api_key_location || 'header') === loc
                        ? 'border-primary bg-primary/5'
                        : ''
                    }`}
                  >
                    {loc}
                  </button>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {authType === 'basic' && (
        <Card>
          <CardHeader>
            <CardTitle>Basic Authentication</CardTitle>
            <CardDescription>
              Configure field names for username and password
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="username-field">Username Field Name</Label>
                <Input
                  id="username-field"
                  value={authConfig.username_field || 'username'}
                  onChange={(e) => updateAuthConfig({ username_field: e.target.value })}
                  placeholder="username"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="password-field">Password Field Name</Label>
                <Input
                  id="password-field"
                  value={authConfig.password_field || 'password'}
                  onChange={(e) => updateAuthConfig({ password_field: e.target.value })}
                  placeholder="password"
                />
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {authType === 'bearer' && (
        <Card>
          <CardHeader>
            <CardTitle>Bearer Token</CardTitle>
            <CardDescription>
              Configure the token prefix
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              <Label htmlFor="token-prefix">Token Prefix</Label>
              <Input
                id="token-prefix"
                value={authConfig.token_prefix || 'Bearer'}
                onChange={(e) => updateAuthConfig({ token_prefix: e.target.value })}
                placeholder="Bearer"
              />
              <p className="text-sm text-muted-foreground">
                The token will be sent as: <code className="bg-muted px-1 rounded">Authorization: {authConfig.token_prefix || 'Bearer'} {'<token>'}</code>
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {authType === 'oauth2' && (
        <Card>
          <CardHeader>
            <CardTitle>OAuth 2.0 Configuration</CardTitle>
            <CardDescription>
              OAuth configuration is managed separately. Go to the OAuth tab after saving the connector.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground">
              Save this connector first, then configure OAuth settings in the OAuth configuration section.
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
